import type { BatchSummary } from './types';

export const IMPORT_BASE = '/payroll/import-payroll-data';
export const HISTORY_PATH = `${IMPORT_BASE}/history`;
export const NEW_IMPORT_PATH = `${IMPORT_BASE}/new`;
/** Step 1 of the wizard, optionally with a provider pre-selected (used by the Back links of later steps). */
export const newImportPath = (providerId?: string | null): string =>
  providerId ? `${NEW_IMPORT_PATH}?provider=${encodeURIComponent(providerId)}` : NEW_IMPORT_PATH;

/**
 * The nine steps of the guided workflow, in order. Step 1 has no batch yet (it lives at /new); every later step
 * belongs to a batch and lives at /:batchId/:step:
 *   1 choose source   (/new)
 *   2 upload          3 found         4 map (guided mapping only)   5 employees
 *   6 totals          7 problems      8 confirm                     9 results
 */
export type BatchStep = 'upload' | 'found' | 'map' | 'employees' | 'totals' | 'problems' | 'confirm' | 'results';
export const BATCH_STEPS: BatchStep[] = ['upload', 'found', 'map', 'employees', 'totals', 'problems', 'confirm', 'results'];

/** Addresses used before the workflow had nine steps; they still open the step that took their place. */
const LEGACY_STEP_ALIASES: Record<string, BatchStep> = { validate: 'employees', preview: 'totals' };

export const batchStepPath = (batchId: string, step: BatchStep): string => `${IMPORT_BASE}/${batchId}/${step}`;
export const historyRunPath = (runId: string): string => `${HISTORY_PATH}/${runId}`;

export function isBatchStep(value: string | undefined): value is BatchStep {
  return BATCH_STEPS.some((s) => s === value);
}

/** The current step an old-style address stands for, or undefined. */
export function legacyStep(value: string | undefined): BatchStep | undefined {
  return value === undefined ? undefined : LEGACY_STEP_ALIASES[value];
}

/** The step a user should land on when (re)opening a batch. */
export function stepForBatch(batch: Pick<BatchSummary, 'status'>): BatchStep {
  switch (batch.status) {
    case 'Created':
    case 'Uploaded':
    case 'Validating':
      return 'upload';
    case 'ColumnMappingRequired':
      return 'map';
    case 'ValidationFailed':
    case 'ReadyForReview':
      // A failed batch lands here too: this step lists what is blocking and offers the ways back
      // (fix employee matches, fix problems, change the column mapping).
      return 'employees';
    default:
      // Importing and every terminal status (Completed, CompletedWithWarnings, Failed, Canceled).
      return 'results';
  }
}

/** Guided-mapping imports ("GenericMapped:<provider>") need the column-mapping step; native EPN formats do not. */
export function usesGuidedMapping(batch: Pick<BatchSummary, 'sourceSystem'>): boolean {
  return batch.sourceSystem === 'GenericMapped' || batch.sourceSystem.startsWith('GenericMapped:');
}
